import { getDateWithoutTimezoneShift } from '@lender/utils';
import _ from 'lodash';
import { normalize, schema } from 'normalizr';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalObject } from 'utils/data';
import { getCommonDateBody } from 'utils/date';
import { isFieldValueChanged } from 'utils/loanFees';
import {
  getDefaultDisbursementDate,
  getDefaultInterestCreditFirstPaymentDate,
} from 'utils/loanPrepaidsDates';

const getPrepaidForSend = formPrepaid => ({
  months: formPrepaid.months.value,
  feeProviderId: formPrepaid.feeProviderId?.value,
  annualAmount: Number(formPrepaid.annualAmount),
  totalCollected: formPrepaid.totalCollected,
});

const isChangedPrepaid = formPrepaid =>
  [
    isFieldValueChanged(formPrepaid, 'feeProviderId'),
    isFieldValueChanged(formPrepaid, 'annualAmount'),
    isFieldValueChanged(formPrepaid, 'totalCollected'),
    isFieldValueChanged(formPrepaid, 'months'),
  ].some(Boolean);

export const getPrepaidsForUpdate = prepaidsFormSet =>
  prepaidsFormSet.reduce(
    (acc, fee) => {
      if (fee.pendingChangesActionType === PendingChangesActionType.DELETED) {
        acc.remove.push(fee.feeTypeId.value);

        return acc;
      }

      if (fee.isLocallyAdded) {
        acc.create[fee.feeTypeId.value] = getPrepaidForSend(fee);

        return acc;
      }

      if (isChangedPrepaid(fee)) {
        acc.update[fee.feeTypeId.value] = getPrepaidForSend(fee);
      }

      return acc;
    },
    { create: {}, update: {}, remove: [] },
  );

export const getInitialPrepaids = ({
  loanViewPrepaids,
  viewItems,
  feesOptions,
  feeProviderOptions,
}) => {
  // till loanViewPrepaids don't have pendingChangesActionType and modifiedFields fields
  const viewFeesSchema = new schema.Entity('data', {}, { idAttribute: 'feeTypeId' });
  const normalizedViewFees = normalize(viewItems, [viewFeesSchema]).entities.data || {};

  return loanViewPrepaids.map(fee => {
    const { pendingChangesActionType = PendingChangesActionType.NO_CHANGES, modifiedFields = [] } =
      normalizedViewFees[fee.feeTypeId] || {};

    const formattedFee = {
      ...fee,
      feeId: fee.feeTypeId,
      annualAmount: String(fee.annualAmount),
      totalCollected: String(fee.totalCollected),
      feeTypeName: fee.feeName,
      feeTypeId: feesOptions.find(i => i.value === fee.feeTypeId),
      feeProviderId: feeProviderOptions.find(i => i.value === fee.feeProviderId),
      months: { name: String(fee.months), value: fee.months },
      pendingChangesActionType,
      modifiedFields,
    };

    return {
      ...formattedFee,
      initialFee: formattedFee,
    };
  });
};

export const getIsPerDiemChanged = ({ form, loanPendingChanges, isHeloc }) => {
  const pendingChange = _.get(loanPendingChanges, 'manualDailyPrepaidInterest');
  const originalValue = pendingChange
    ? _.get(pendingChange, 'initialValue')
    : _.get(form, ['set_interestCredit', 'initialManualDailyPrepaidInterest']);
  const currentValue = _.get(form, ['set_interestCredit', 'manualDailyPrepaidInterest']);

  return isHeloc && `${currentValue}` !== `${originalValue}`;
};

// The fees summary serializes the funding date as UTC midnight; reading it in local time would
// land a US user on the previous day and move a HELOC funded on the 15th to the earlier month.
// A HELOC's or a chattel loan's default ignores interest credit, so re-defaulting on a prepaid
// save would only discard a first payment date the user set by hand (LEN-198 for chattel).
export const getPrepaidInterestCreditFirstPaymentDate = ({
  hasInterestCredit,
  fundingDate,
  isHeloc,
  isChattel = false,
  storedFirstPaymentDate,
  businessDays,
}) => {
  if ((isHeloc || isChattel) && storedFirstPaymentDate) {
    return getCommonDateBody(getDateWithoutTimezoneShift(storedFirstPaymentDate));
  }

  return getCommonDateBody(
    getDefaultInterestCreditFirstPaymentDate({
      hasInterestCredit,
      interestCreditFundingDate: getDefaultDisbursementDate(
        fundingDate && getDateWithoutTimezoneShift(fundingDate),
        businessDays,
      ),
      isHeloc,
      ...getConditionalObject(isChattel, { isChattel }),
    }),
  );
};
